"use client";

import { AudioGenerationFlow } from "@/components/AudioGenerationFlow";
import { api } from "convex/_generated/api";
import { useQuery } from "convex/react";
import Link from "next/link";
import { useMemo } from "react";
import { Suspense } from "react";
import { Clock3 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import type { Id } from "convex/_generated/dataModel";

function NewAudioContent() {
  const searchParams = useSearchParams();
  const me = useQuery(api.users.getMe);
  const creditInfo = useQuery(api.creditRequests.getMine);

  const billing = creditInfo?.billing;
  const canUseAudio = billing?.hasProGatedFeatureAccess ?? false;

  const gate = useMemo(() => {
    if (me === undefined || creditInfo === undefined) return "loading" as const;
    if (me === null) return "sync" as const;
    return "ready" as const;
  }, [me, creditInfo]);

  if (gate === "loading" || gate === "sync") {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
        <div className="h-9 w-9 rounded-full border-2 border-border border-t-primary dash-spin" aria-hidden />
        <p className="text-sm text-muted-foreground">
          {gate === "sync" ? "Preparing your account…" : "Loading…"}
        </p>
      </div>
    );
  }

  return (
    <main className="flex w-full flex-col gap-5 pb-8">

      {/* ── Page header ────────────────────────────────────── */}
      <div className="dash-panel-in flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Audio
          </h1>
        </div>
        <Link
          href="/history"
          className="inline-flex min-h-11 w-fit shrink-0 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success"
        >
          <Clock3 className="h-4 w-4 text-muted-foreground" />
          My resources
        </Link>
      </div>

      {/* ── Main content ───────────────────────────────────── */}
      <div className="dash-panel-in dash-stagger-1">
        <AudioGenerationFlow
          canUseAudio={canUseAudio}
          trialAudioComplimentary={billing?.trialEligible === true}
          initialGenerationId={(searchParams.get("resume") as Id<"audioGenerations"> | null) ?? undefined}
        />
      </div>
    </main>
  );
}

export default function NewAudioPage() {
  return <Suspense fallback={<div role="status" className="py-16 text-center text-sm text-muted-foreground">Loading Audio…</div>}><NewAudioContent /></Suspense>;
}
